import { Record } from 'immutable';
import { App } from '@gin-betting/core';
import { persistReducer } from '@gin-betting/persist';

import { isMobile, SidebarView } from 'app-constants';
import {
    CHANGE_ODD_FORMAT,
    CHANGE_SIDEBAR_VIEW,
    CHANGE_FAVORITE_COUNT,
    ENABLE_ODD_HISTORY,
    DISABLE_ODD_HISTORY,
    CHANGE_ODDS_STATISTIC,
    OPEN_SIDEBAR,
    CLOSE_SIDEBAR,
    OPEN_MOBILE_TOP_MATCHES,
    CLOSE_MOBILE_TOP_MATCHES,
    OPEN_FILTER,
    CLOSE_FILTER,
    SET_ACTIVE_SPORT_TYPE,
} from './constants';
import { UIState, UIStateShape } from './types';
import { Actions } from './actions';

const UIStateRecord = Record<UIStateShape>(
    {
        oddFormat: App.OddFormat.Decimal,
        sidebarView: isMobile ? SidebarView.betslip : SidebarView.promo,
        isSidebarOpen: false,
        streamViewerVisibility: 'visible',
        favoriteCount: {
            upcoming: null,
            outrights: null,
        },
        isOddHistoryEnabled: false,
        oddsStatistic: {
            statisticView: App.OddStatisticView.MARKET,
            show: false,
        },
        isMobileTopMatchesOpened: false,
        isFilterOpened: false,
        activeSportType: null,
    },
    'UIState'
);

const initialState: UIState = new UIStateRecord();

function reducer(state = initialState, action: Actions): UIState {
    switch (action.type) {
        case OPEN_SIDEBAR: {
            return state.set('isSidebarOpen', true);
        }

        case CLOSE_SIDEBAR: {
            return state.set('isSidebarOpen', false);
        }

        case CHANGE_FAVORITE_COUNT: {
            const { count, type } = action.payload;

            return state.setIn(['favoriteCount', type], count);
        }

        case CHANGE_ODD_FORMAT: {
            const { oddFormat } = action.payload;

            return state.set('oddFormat', oddFormat);
        }

        case CHANGE_SIDEBAR_VIEW: {
            const { sidebarView } = action.payload;

            return state.set('sidebarView', sidebarView);
        }

        case ENABLE_ODD_HISTORY: {
            return state.set('isOddHistoryEnabled', true);
        }

        case DISABLE_ODD_HISTORY: {
            return state.set('isOddHistoryEnabled', false);
        }

        case CHANGE_ODDS_STATISTIC: {
            const { statisticView, show } = action.payload;

            const oddsStatistic = {
                statisticView:
                    statisticView || state.oddsStatistic.statisticView,
                show: show !== undefined ? show : state.oddsStatistic.show,
            };

            return state.set('oddsStatistic', oddsStatistic);
        }

        case OPEN_MOBILE_TOP_MATCHES: {
            return state.set('isMobileTopMatchesOpened', true);
        }

        case CLOSE_MOBILE_TOP_MATCHES: {
            return state.set('isMobileTopMatchesOpened', false);
        }

        case OPEN_FILTER: {
            return state.set('isFilterOpened', true);
        }

        case CLOSE_FILTER: {
            return state.set('isFilterOpened', false);
        }

        case SET_ACTIVE_SPORT_TYPE: {
            return state.set('activeSportType', action.payload);
        }

        default:
            return state;
    }
}

export { reducer };

export default persistReducer<UIState>(
    {
        key: 'ui',
        stateReconciler: (inboundState, _, reducedState) => {
            return Object.keys(inboundState).reduce((persistedState, key) => {
                if (!reducedState.has(key)) return persistedState;

                return persistedState.set(key, inboundState[key]);
            }, reducedState);
        },
        blacklist: [
            'isSidebarOpen',
            'isMobileTopMatchesOpened',
            'sidebarView',
            'favoriteCount',
            'activeSportType',
            'matchListView',
        ],
    },
    reducer
);
